import React from "react";
import DonutChart from "../../Components/Charts/DonutChart";
import MultiTrendChart from "../../Components/Charts/MultiTrendChart";
import StackedBar from "../../Components/Charts/StackedBar";
import {
  AlertTriangleIcon,
  ArrowRightIcon,
  ArrowUpRightIcon,
  BarsIcon,
  CalendarIcon,
  CheckCircleIcon,
  CopyIcon,
  DatabaseIcon,
  DocCheckIcon,
  DownloadIcon,
  EyeIcon,
  LightbulbIcon,
  LinkIcon,
  MoreIcon,
  NetworkIcon,
  PulseIcon,
  SettingsIcon,
  ShieldCheckIcon,
  SparkleIcon,
  TransferIcon,
  TrendUpIcon,
} from "../../Components/Icons";
import {
  AI_FINDINGS,
  AI_INSIGHTS,
  ANALYSIS_DATES,
  ANALYZED_RECORDS,
  AUDIT_STATS,
  BANDS,
  KEY_RISK_AREAS,
  QUICK_ACTIONS,
  RECOMMENDED_ACTIONS,
  RISK_DISTRIBUTION,
  RISK_TREND,
  SECTOR_RISK,
  TOTAL_FINDINGS,
  TREND_MAX,
} from "./data";
import "./AuditIntelligence.css";

const STAT_ICONS = {
  shield: ShieldCheckIcon,
  alert: AlertTriangleIcon,
  pulse: PulseIcon,
  network: NetworkIcon,
  database: DatabaseIcon,
};

const AREA_ICONS = {
  copy: CopyIcon,
  trend: TrendUpIcon,
  doc: DocCheckIcon,
  transfer: TransferIcon,
  link: LinkIcon,
};

const QUICK_ICONS = {
  sparkle: SparkleIcon,
  bars: BarsIcon,
  download: DownloadIcon,
  calendar: CalendarIcon,
};

const chipClass = (value) =>
  `chip chip--${value.toLowerCase().replace(/\s+/g, "-")}`;

function CardHeader({ title, action }) {
  return (
    <div className="dgi-card__header">
      <h2 className="dgi-card__title">{title}</h2>
      {action}
    </div>
  );
}

/** Shared legend for the four severity bands. */
function BandLegend() {
  return (
    <ul className="band-legend">
      {BANDS.map((band) => (
        <li key={band.key}>
          <span className="donut__swatch" style={{ background: band.color }} />
          {band.label}
        </li>
      ))}
    </ul>
  );
}

export default function AuditIntelligence() {
  return (
    <div className="page">
      {/* --------------------------------------------------------- head -- */}
      <div className="page__head">
        <div>
          <h1 className="page__title">Audit Intelligence</h1>
          <p className="page__subtitle">
            AI-powered insights to identify risks, anomalies, and patterns
            across taxpayer data.
          </p>
        </div>

        <div className="page__actions">
          <button type="button" className="dgi-btn dgi-btn--ghost">
            <SettingsIcon size={17} />
            AI Settings
          </button>
          <button type="button" className="dgi-btn dgi-btn--primary">
            <SparkleIcon size={17} />
            Generate New Analysis
          </button>
        </div>
      </div>

      {/* -------------------------------------------------------- stats -- */}
      <section className="row g-3" aria-label="Analysis summary">
        {AUDIT_STATS.map((card) => {
          const Icon = STAT_ICONS[card.icon];
          return (
            <div className="col-6 col-lg-4 col-xxl" key={card.key}>
              <article className="ai-stat h-100">
                <span className={`kpi__icon kpi__icon--${card.tone}`}>
                  <Icon size={22} />
                </span>
                <div className="ai-stat__meta">
                  <p className="kpi__label">{card.label}</p>
                  <p className="ai-stat__value">
                    {card.value}
                    {card.unit && <span>{card.unit}</span>}
                  </p>
                  {card.sub && (
                    <p
                      className={`ai-stat__sub${
                        card.subTone ? ` ai-stat__sub--${card.subTone}` : ""
                      }`}
                    >
                      {card.sub}
                    </p>
                  )}
                  {card.delta && (
                    <p className="ai-stat__delta">
                      <span className="kpi__delta kpi__delta--warn">
                        <ArrowUpRightIcon size={12} />
                        {card.delta}
                      </span>
                      {card.deltaNote}
                    </p>
                  )}
                </div>
              </article>
            </div>
          );
        })}
      </section>

      {/* ---------------------------------------------- main two columns -- */}
      <div className="ai-main">
        <div className="stack">
          {/* ------------------------------------------ key risk areas -- */}
          <section className="dgi-card">
            <CardHeader
              title="Key Risk Areas (AI-Identified)"
              action={
                <button
                  type="button"
                  className="dgi-card__link dgi-card__link--inline"
                >
                  View all
                </button>
              }
            />
            <div className="row g-3">
              {KEY_RISK_AREAS.map((area) => {
                const Icon = AREA_ICONS[area.icon];
                return (
                  <div className="col-12 col-sm-6 col-xxl-3" key={area.key}>
                    <article className="area h-100">
                      <span className={`kpi__icon kpi__icon--${area.tone}`}>
                        <Icon size={20} />
                      </span>
                      <p className="area__title">{area.title}</p>
                      <p className="area__text">{area.text}</p>
                      <p className="area__foot">
                        Risk Level
                        <span
                          className={`area__level area__level--${area.level.toLowerCase()}`}
                        >
                          {area.level}
                        </span>
                      </p>
                    </article>
                  </div>
                );
              })}
            </div>
          </section>

          {/* --------------------------------------------- top findings -- */}
          <section className="dgi-card">
            <CardHeader title="Top AI Findings" />

            <div className="dgi-table-wrap">
              <table className="dgi-table dgi-table--findings">
                <thead>
                  <tr>
                    <th>Finding</th>
                    <th>Risk Level</th>
                    <th>Affected Taxpayers</th>
                    <th>Confidence Score</th>
                    <th>First Detected</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {AI_FINDINGS.map((finding) => {
                    const Icon = AREA_ICONS[finding.icon];
                    return (
                      <tr key={finding.id}>
                        <td>
                          <span className="finding">
                            <span className="finding__icon">
                              <Icon size={16} />
                            </span>
                            {finding.name}
                          </span>
                        </td>
                        <td>
                          <span className={chipClass(finding.level)}>
                            {finding.level}
                          </span>
                        </td>
                        <td>{finding.affected}</td>
                        <td>
                          <span className="confidence">
                            <span className="score__track confidence__track">
                              <span
                                className="score__fill score__fill--good"
                                style={{ width: `${finding.confidence}%` }}
                              />
                            </span>
                            {finding.confidence}%
                          </span>
                        </td>
                        <td>{finding.detected}</td>
                        <td>
                          <span className={chipClass(finding.status)}>
                            {finding.status}
                          </span>
                        </td>
                        <td>
                          <span className="row-actions">
                            <button
                              type="button"
                              className="row-actions__btn"
                              aria-label={`View ${finding.name}`}
                            >
                              <EyeIcon size={17} />
                            </button>
                            <button
                              type="button"
                              className="dgi-card__more"
                              aria-label={`Actions for ${finding.name}`}
                            >
                              <MoreIcon size={18} />
                            </button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="egraph__foot">
              <p className="listbar__count">
                Showing 1 to {AI_FINDINGS.length} of {TOTAL_FINDINGS} findings
              </p>
              <button
                type="button"
                className="dgi-card__link dgi-card__link--inline"
              >
                View all findings <ArrowRightIcon size={16} />
              </button>
            </div>
          </section>

          {/* --------------------------------------- trend + sector risk -- */}
          <div className="row g-3">
            <div className="col-12 col-xxl-6">
              <section className="dgi-card h-100">
                <CardHeader title="Risk Trend (Last 6 Analyses)" />
                <MultiTrendChart
                  labels={ANALYSIS_DATES}
                  series={RISK_TREND}
                  max={TREND_MAX}
                  formatTick={(value) => `${Math.round(value)}%`}
                  formatValue={(value) => `${value}%`}
                />
              </section>
            </div>

            <div className="col-12 col-xxl-6">
              <section className="dgi-card h-100">
                <CardHeader title="Risk by Sector (Top 5)" />
                <BandLegend />
                <StackedBar rows={SECTOR_RISK} bands={BANDS} />
                <button type="button" className="dgi-card__link">
                  View full sector analysis <ArrowRightIcon size={16} />
                </button>
              </section>
            </div>
          </div>
        </div>

        {/* ------------------------------------------------ right column -- */}
        <div className="stack">
          <section className="dgi-card">
            <CardHeader title="Audit Risk Distribution" />
            <DonutChart
              segments={RISK_DISTRIBUTION}
              total={ANALYZED_RECORDS}
              totalLabel="Analyzed Records"
            />
            <button type="button" className="dgi-card__link">
              View full risk distribution <ArrowRightIcon size={16} />
            </button>
          </section>

          <section className="dgi-card">
            <div className="dgi-card__header">
              <h2 className="dgi-card__title ai-insights__title">
                <span className="ai-insights__icon">
                  <LightbulbIcon size={18} />
                </span>
                AI Insights
              </h2>
            </div>
            <ul className="ai-insights">
              {AI_INSIGHTS.map((insight) => (
                <li key={insight}>{insight}</li>
              ))}
            </ul>
            <button type="button" className="dgi-card__link">
              View full insights <ArrowRightIcon size={16} />
            </button>
          </section>

          <section className="dgi-card">
            <CardHeader title="Recommended Actions" />
            <ul className="recs">
              {RECOMMENDED_ACTIONS.map((action) => (
                <li className="rec" key={action.title}>
                  <span className="rec__icon">
                    <CheckCircleIcon size={17} />
                  </span>
                  <div>
                    <p className="rec__title">{action.title}</p>
                    <p className="rec__text">{action.text}</p>
                  </div>
                </li>
              ))}
            </ul>
            <button type="button" className="dgi-card__link">
              View all recommended actions <ArrowRightIcon size={16} />
            </button>
          </section>

          <section className="dgi-card">
            <CardHeader title="Quick Actions" />
            <div className="quick-actions quick-actions--wide">
              {QUICK_ACTIONS.map((action) => {
                const Icon = QUICK_ICONS[action.icon];
                return (
                  <button
                    type="button"
                    className="quick-action"
                    key={action.key}
                  >
                    <Icon size={19} />
                    <span>{action.label}</span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
